import type { RejectAssignmentRequest, ResolveUnclearAssignmentRequest } from '@bliks-io/contracts';
import type { ProcessStepAssignment } from '@/api/queries/useTasks';
import type { ReassignPayload } from '@/lib/successorEntityMapping';
import { getProcessId } from './assignmentHelpers';

interface ReassignmentMutations {
  rejectMutation: {
    mutateAsync: (data: {
      assignmentId: string;
      processId: string;
      data: RejectAssignmentRequest;
    }) => Promise<void>;
  };
  resolveUnclearMutation: {
    mutateAsync: (data: {
      assignmentId: string;
      processId: string;
      data: ResolveUnclearAssignmentRequest;
    }) => Promise<void>;
  };
}

export async function handleReassignment(
  assignment: ProcessStepAssignment | null,
  data: ReassignPayload,
  mutations: ReassignmentMutations
): Promise<void> {
  if (!assignment) {
    return;
  }

  // Route to correct endpoint based on assignment status
  if (assignment.status === 'pending-unclear') {
    // PENDING_UNCLEAR: Use resolve-unclear-assignment endpoint
    // This creates a new chained assignment and marks the unclear one as resolved
    if (!data.reAssigneeId || !data.reAssigneeModel) {
      console.error(
        'reAssigneeId and reAssigneeModel are required for resolving unclear assignments'
      );
      return;
    }
    await mutations.resolveUnclearMutation.mutateAsync({
      assignmentId: assignment._id,
      processId: getProcessId(assignment.process),
      data: {
        reAssigneeId: data.reAssigneeId,
        reAssigneeModel: data.reAssigneeModel,
        reAssigneeType: data.reAssigneeType
      }
    });
  } else {
    // PENDING: Use reject-assignment endpoint
    // This rejects the assignment and creates a new one via event handler
    await mutations.rejectMutation.mutateAsync({
      assignmentId: assignment._id,
      processId: getProcessId(assignment.process),
      data: {
        rejectReason: data.isUnclear ? 'unclear' : 're-assign',
        reAssigneeId: data.reAssigneeId,
        reAssigneeModel: data.reAssigneeModel,
        reAssigneeType: data.reAssigneeType
      }
    });
  }
}
